import { useCallback, useEffect, useRef, useState } from "react";

import {
	useCreateSumSubTokenQuery,
	useSumSubSocket,
	useSumSubVerificationStatusMutation,
	useValidateSumSubTokenMutation,
} from "@api";

const useController = () => {
	const [open, setOpen] = useState(false);
	const [isLoading, setIsLoading] = useState(false);

	const inFlightRef = useRef(false);

	const onRejectRef = useRef(null);
	const onResolveRef = useRef(null);
	const originalDateRef = useRef(null);
	const originalActionRef = useRef(null);
	const originalParamsRef = useRef(null);

	const [verificationType, setVerificationType] = useState(null);
	const [verificationRequest, setVerificationRequest] = useState(null);

	const {
		data: obtainedSumSubToken,
		isError: isSumSubTokenError,
		isLoading: isSumSubTokenLoading,
	} = useCreateSumSubTokenQuery(
		{
			username: verificationRequest?.username,
			email: verificationRequest?.email,
			timestamp: originalDateRef.current,
		},
		{
			skip: !open || !verificationRequest,
		}
	);

	const [validateToken] = useValidateSumSubTokenMutation();
	const [getVerificationStatus] = useSumSubVerificationStatusMutation();

	const {
		isError: isSocketError,
		lastMessage,
		closeSocket,
	} = useSumSubSocket(
		{
			token: obtainedSumSubToken,
			signup: verificationType === "initial",
			username: verificationRequest?.username,
		},
		{
			skip: !open || !verificationRequest || !obtainedSumSubToken,
		}
	);

	const cleanRefs = () => {
		setVerificationRequest(null);
		originalActionRef.current = null;
		originalParamsRef.current = null;
		onRejectRef.current = null;
		onResolveRef.current = null;
		originalDateRef.current = null;
		inFlightRef.current = false;
		closeSocket();
	};

	const withVerification = useCallback(
		(action, params) =>
			new Promise((resolve, reject) => {
				try {
					if (inFlightRef.current) return;
					inFlightRef.current = true;
					action(params)
						.then((response) => {
							if (response && "manuallink" in response) {
								onRejectRef.current = reject;
								onResolveRef.current = resolve;
								originalActionRef.current = action;
								originalParamsRef.current = params;
								originalDateRef.current = Date.now();

								setVerificationType("manual");
								setVerificationRequest({
									username: response.username,
									email: response.email,
									weblink: response.manuallink,
								});
								setOpen(true);

								return;
							}

							if (
								response &&
								"message" in response &&
								"weblink" in response.message
							) {
								onRejectRef.current = reject;
								onResolveRef.current = resolve;
								originalActionRef.current = action;
								originalParamsRef.current = params;
								originalDateRef.current = Date.now();

								setVerificationRequest(response.message);
								setVerificationType(
									response.message.verificationType || "recurring"
								);
								setOpen(true);

								return;
							}

							inFlightRef.current = false;
							return resolve(response);
						})
						.catch((error) => {
							inFlightRef.current = false;
							reject(error || new Error("verification.action_failed"));
						});
				} catch {
					inFlightRef.current = false;
					reject(new Error("verification.unknown_error"));
				}
			}),
		[]
	);

	const handleVerificationSuccess = async (success) => {
		if (!success) {
			onRejectRef.current(new Error("verification.failed"));
			return;
		}
		try {
			const response = await originalActionRef.current({
				...originalParamsRef.current,
				hash: verificationRequest?.hash,
				email: verificationRequest?.email,
				requestId: verificationRequest?.requestId,
			});

			onResolveRef.current(response);
		} catch (error) {
			onRejectRef.current(new Error("verification.origin_request_failed"));
		}
	};

	const handleVerificationClose = () => {
		if (onRejectRef.current) {
			onRejectRef.current(new Error("verification.closed"));
		}

		cleanRefs();
		setOpen(false);
	};

	const handleVerificationIframeError = () => {
		if (onRejectRef.current) {
			onRejectRef.current(new Error("verification.iframe_error"));
		}

		cleanRefs();
		setOpen(false);
	};

	useEffect(() => {
		if (lastMessage) {
			const { id, type } = JSON.parse(lastMessage);

			setIsLoading(true);

			validateToken({
				token: obtainedSumSubToken,
				username: verificationRequest?.username,
				email: verificationRequest?.email,
			})
				.then(() =>
					getVerificationStatus({
						id,
						type,
						username: verificationRequest?.username,
						email: verificationRequest?.email,
						sumsubToken: obtainedSumSubToken,
					}).unwrap()
				)
				.then((verificationStatus) => {
					if (
						verificationType === "initial" &&
						verificationStatus?.reviewResult?.reviewAnswer === "GREEN"
					) {
						return true;
					}

					if (
						Array.isArray(verificationStatus?.list?.items) &&
						verificationStatus.list.items.length > 0
					) {
						const currentVerification = verificationStatus.list.items.find(
							(verification) => verification.id === id
						);

						if (currentVerification.reviewResult.reviewAnswer === "GREEN") {
							return true;
						}
					}

					return false;
				})
				.then(handleVerificationSuccess)
				.catch(() => {
					setIsLoading(false);

					if (onRejectRef.current) {
						onRejectRef.current(new Error("verification.failed"));
					}
				})
				.finally(() => {
					cleanRefs();
					setOpen(false);
				});
		}
	}, [lastMessage]);

	useEffect(() => {
		if (isSocketError && onRejectRef.current) {
			onRejectRef.current(new Error("verification.socket_error"));
			setOpen(false);
			cleanRefs();
		}

		if (isSumSubTokenError && onRejectRef.current) {
			onRejectRef.current(new Error("verification.token_error"));
			setOpen(false);
			cleanRefs();
		}
	}, [isSocketError, isSumSubTokenError]);

	return {
		state: {
			open,
			isLoading,
			verificationRequest,
			isSumSubTokenLoading,
		},
		handlers: {
			withVerification,
			handleVerificationClose,
			handleVerificationIframeError,
		},
	};
};

export default useController;
